import type {
  BenefitItem,
  GlobalCoverageItem,
} from '@/services/coverages/types';
import type {
  CapitalGlobalAssistanceCatalogItem,
  CapitalGlobalCoverageBenefitOption,
  CapitalGlobalCoverageCatalogItem,
  CapitalGlobalSelectOption,
} from './coverages-catalog';
import {
  isGroupedAssistanceBenefit,
  isStandaloneAssistanceBenefit,
} from './coverages-catalog';

/**
 * Capital em reais a partir do NOME da opção de benefício ("R$ 5.000",
 * "R$ 7.000,50"). O catálogo do InsureMO não traz o valor numérico do nível —
 * só o nome. `null` quando o nome não é monetário (ex.: "30 Diárias de
 * Incapacidade Temporária").
 */
export function parseBenefitCapitalFromName(name: string): number | null {
  const match = name.trim().match(/^R\$\s*([\d.]+)(?:,(\d{1,2}))?$/);
  if (!match) return null;

  const integerPart = parseInt(match[1].replace(/\./g, ''), 10);
  if (!Number.isFinite(integerPart)) return null;

  const cents = match[2] ? parseInt(match[2].padEnd(2, '0'), 10) / 100 : 0;
  return integerPart + cents;
}

function mapCoverageBenefitOptions(
  coverage: GlobalCoverageItem
): CapitalGlobalCoverageBenefitOption[] {
  return (coverage.benefits ?? [])
    .slice()
    .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
    .map(benefit => {
      const productElementId = Number(benefit.id);
      return {
        code: benefit.code,
        name: benefit.name,
        capital: parseBenefitCapitalFromName(benefit.name),
        ...(Number.isFinite(productElementId) ? { productElementId } : {}),
      };
    });
}

export function mapCoverageToCapitalGlobalCoverage(
  coverage: GlobalCoverageItem
): CapitalGlobalCoverageCatalogItem {
  const minRaw = coverage.limit?.constraints.min;
  const maxRaw = coverage.limit?.constraints.max;
  const limitMin =
    coverage.limit != null && minRaw != null && Number.isFinite(minRaw)
      ? minRaw
      : null;
  const limitMax =
    coverage.limit != null && maxRaw != null && Number.isFinite(maxRaw)
      ? maxRaw
      : null;
  const referenceCoverageCode =
    coverage.referenceCoverageCode ?? coverage.ReferenceCoverageCode;
  const parentCoverageCode =
    coverage.parentCoverageCode ?? coverage.ParentCoverageCode;
  const proportion = coverage.proportion ?? coverage.Proportion;
  const benefitOptions = mapCoverageBenefitOptions(coverage);

  return {
    id: coverage.id,
    code: coverage.code,
    productElementId: Number(coverage.id),
    title: coverage.name,
    description: coverage.description,
    required: coverage.required,
    limitMin,
    limitMax,
    partnerOnly: coverage.partnerOnly ?? false,
    ...(coverage.category?.trim()
      ? { category: coverage.category.trim() }
      : {}),
    ...(coverage.sumInsuredDefinition
      ? { sumInsuredDefinition: coverage.sumInsuredDefinition }
      : {}),
    ...(benefitOptions.length ? { benefitOptions } : {}),
    ...(referenceCoverageCode ? { referenceCoverageCode } : {}),
    ...(parentCoverageCode ? { parentCoverageCode } : {}),
    ...(coverage.relationType ? { relationType: coverage.relationType } : {}),
    ...(coverage.exclusionGroupCode
      ? { exclusionGroupCode: coverage.exclusionGroupCode }
      : {}),
    ...(proportion != null && Number.isFinite(proportion)
      ? { proportion }
      : {}),
  };
}

export function mapCoveragesToCapitalGlobalCoverageCatalog(
  coverages: GlobalCoverageItem[]
): CapitalGlobalCoverageCatalogItem[] {
  const sorted = [...coverages].sort(
    (a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)
  );
  return sorted.map(mapCoverageToCapitalGlobalCoverage);
}

/** Maps standalone assistance benefits to catalog toggles. */
export function mapAssistancesToCapitalGlobalAssistanceCatalog(
  benefits: BenefitItem[]
): CapitalGlobalAssistanceCatalogItem[] {
  return benefits
    .filter(isStandaloneAssistanceBenefit)
    .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
    .map(b => ({
      code: b.code,
      title: b.name,
      description: '',
      partnerOnly: b.partnerOnly ?? false,
      ...(b.exclusionGroupCode
        ? { exclusionGroupCode: b.exclusionGroupCode }
        : {}),
    }));
}

/** Maps funeral-group benefits to the "Assistência Funeral" select options. */
export function mapAssistancesToSelectOptions(
  benefits: BenefitItem[]
): CapitalGlobalSelectOption[] {
  return benefits
    .filter(isGroupedAssistanceBenefit)
    .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
    .map(b => ({
      value: b.code,
      label: b.name,
      partnerOnly: b.partnerOnly ?? false,
      ...(b.exclusionGroupCode
        ? { exclusionGroupCode: b.exclusionGroupCode }
        : {}),
    }));
}

/** Maps capitalization benefit items to select options. */
export function mapCapitalizationsToSelectOptions(
  benefits: BenefitItem[]
): CapitalGlobalSelectOption[] {
  return benefits
    .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
    .map(b => ({
      value: b.code,
      label: b.name,
      partnerOnly: b.partnerOnly ?? false,
      ...(b.exclusionGroupCode
        ? { exclusionGroupCode: b.exclusionGroupCode }
        : {}),
    }));
}
